import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { Search, Download, DollarSign, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import type { Enrollment, Client, Plan } from "@shared/schema";
import { exportToCSV } from "@/lib/exportUtils";

export default function Premiums() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClient, setFilterClient] = useState("all");
  const [viewType, setViewType] = useState("table");

  const { data: enrollments = [], isLoading: enrollmentsLoading } = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments"],
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ["/api/plans"],
  });

  const getClientName = (clientId: number) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || "Unknown Client";
  };

  const getPlanName = (planId: number) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.name || "Unknown Plan";
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    const clientName = getClientName(enrollment.clientId);
    const planName = getPlanName(enrollment.planId);
    
    const matchesSearch = enrollment.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         planName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = filterClient === "all" || enrollment.clientId.toString() === filterClient;
    
    return matchesSearch && matchesClient && enrollment.status === "active";
  });

  // Calculate premium summaries
  const premiumSummary = {
    totalMonthly: filteredEnrollments.reduce((sum, e) => sum + parseFloat(e.monthlyPremium), 0),
    totalAnnual: filteredEnrollments.reduce((sum, e) => sum + (parseFloat(e.monthlyPremium) * 12), 0),
    averagePerEmployee: filteredEnrollments.length > 0 
      ? filteredEnrollments.reduce((sum, e) => sum + parseFloat(e.monthlyPremium), 0) / filteredEnrollments.length 
      : 0,
    totalEnrollments: filteredEnrollments.length
  };

  // Group by client for charts
  const clientPremiumData = clients.map(client => {
    const clientEnrollments = filteredEnrollments.filter(e => e.clientId === client.id);
    const monthlyTotal = clientEnrollments.reduce((sum, e) => sum + parseFloat(e.monthlyPremium), 0);
    return {
      name: client.name,
      monthly: monthlyTotal,
      enrollments: clientEnrollments.length
    };
  }).filter(item => item.monthly > 0);

  // Group by plan type for analysis
  const planTypeData = plans.map(plan => {
    const planEnrollments = filteredEnrollments.filter(e => e.planId === plan.id);
    const monthlyTotal = planEnrollments.reduce((sum, e) => sum + parseFloat(e.monthlyPremium), 0);
    return {
      name: plan.name,
      type: plan.type,
      monthly: monthlyTotal,
      enrollments: planEnrollments.length
    };
  }).filter(item => item.monthly > 0);

  // Monthly trend data (simplified - in real app would come from backend)
  const monthlyTrendData = [
    { month: "Jan", premium: premiumSummary.totalMonthly * 0.85 },
    { month: "Feb", premium: premiumSummary.totalMonthly * 0.88 },
    { month: "Mar", premium: premiumSummary.totalMonthly * 0.92 },
    { month: "Apr", premium: premiumSummary.totalMonthly * 0.95 },
    { month: "May", premium: premiumSummary.totalMonthly * 0.98 },
    { month: "Jun", premium: premiumSummary.totalMonthly },
  ];

  const exportPremiums = () => {
    const csvData = filteredEnrollments.map(enrollment => ({
      Employee: enrollment.employeeName,
      Client: getClientName(enrollment.clientId),
      Plan: getPlanName(enrollment.planId),
      MonthlyPremium: enrollment.monthlyPremium,
      AnnualPremium: (parseFloat(enrollment.monthlyPremium) * 12).toFixed(2),
      Status: enrollment.status,
      EnrollmentDate: enrollment.enrollmentDate
    }));

    exportToCSV(csvData, {
      filename: `premiums-${new Date().toISOString().split('T')[0]}.csv`,
      headers: ['Employee', 'Client', 'Plan', 'MonthlyPremium', 'AnnualPremium', 'Status', 'EnrollmentDate']
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Premium Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Track and analyze premium payments and trends</p>
        </div>
        <div className="flex space-x-2">
          <Select value={viewType} onValueChange={setViewType}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="table">Table View</SelectItem>
              <SelectItem value="charts">Charts View</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportPremiums}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Premium Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="text-2xl text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Monthly</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    ${premiumSummary.totalMonthly.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="text-2xl text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Annual</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    ${premiumSummary.totalAnnual.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="text-2xl text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Avg per Employee</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    ${premiumSummary.averagePerEmployee.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="text-2xl text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Enrollments</dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {premiumSummary.totalEnrollments}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search premiums..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id.toString()}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      {/* Content based on view type */}
      {viewType === "charts" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Premium by Client Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Premiums by Client</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientPremiumData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, "Monthly Premium"]} />
                    <Bar dataKey="monthly" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Premium Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, "Premium"]} />
                    <Line type="monotone" dataKey="premium" stroke="#059669" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Plan Type Breakdown */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Premium Breakdown by Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {planTypeData.map((plan) => (
                  <div key={plan.name} className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">{plan.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{plan.type}</p>
                    <div className="mt-2">
                      <p className="text-lg font-semibold text-green-600">
                        ${plan.monthly.toLocaleString()}/mo
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {plan.enrollments} enrollments
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Table View */
        <Card>
          <CardContent className="p-0">
            {enrollmentsLoading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Monthly Premium
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Annual Premium
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredEnrollments.map((enrollment) => (
                      <tr key={enrollment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {enrollment.employeeName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {enrollment.employeeEmail}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {getClientName(enrollment.clientId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {getPlanName(enrollment.planId)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          ${parseFloat(enrollment.monthlyPremium).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          ${(parseFloat(enrollment.monthlyPremium) * 12).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className="status-active">
                            {enrollment.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredEnrollments.length === 0 && (
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No premium data found</h3>
                    <p className="text-gray-500 dark:text-gray-400">Premium information will appear here once enrollments are active.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
